// transactionLifecycle.js — Transaction lifecycle state machine
// Enforces valid state transitions and role-based access for the full transaction flow
// Pattern: mirrors src/lib/kyc/stateMachine.js

// ─── Transaction States ────────────────────────────────────────────────
/** @enum {string} */
export const TRANSACTION_STATES = Object.freeze({
  // Registration & KYC
  REGISTERED: 'registered',
  KYC_SUBMITTED: 'kyc_submitted',
  KYC_MAKER_REVIEW: 'kyc_maker_review',
  KYC_MAKER_APPROVED: 'kyc_maker_approved',
  KYC_CHECKER_REVIEW: 'kyc_checker_review',
  KYC_CHECKER_APPROVED: 'kyc_checker_approved',
  KYC_REJECTED: 'kyc_rejected',

  // CDS Card Signing
  CDS_CARD_PENDING: 'cds_card_pending',
  CDS_CARD_CHECKER_SIGNED: 'cds_card_checker_signed',
  CDS_CARD_MAKER_SIGNED: 'cds_card_maker_signed',
  CDS_CARD_COMPLETE: 'cds_card_complete',
  CDS_CARD_REJECTED: 'cds_card_rejected',

  // Auction & Bidding
  AUCTION_ELIGIBLE: 'auction_eligible',
  BID_PLACED: 'bid_placed',
  BID_ALLOCATED: 'bid_allocated',
  BID_UNSUCCESSFUL: 'bid_unsuccessful',

  // Settlement & FX
  SETTLEMENT_PENDING: 'settlement_pending',
  FX_RFQ_SENT: 'fx_rfq_sent',
  FX_QUOTES_RECEIVED: 'fx_quotes_received',
  FX_RATE_LOCKED: 'fx_rate_locked',
  FX_RATE_EXPIRED: 'fx_rate_expired',

  // Bank Instructions
  BANK_INSTRUCTION_SENT: 'bank_instruction_sent',
  BANK_INSTRUCTION_CONFIRMED: 'bank_instruction_confirmed',
  BANK_INSTRUCTION_REJECTED: 'bank_instruction_rejected',

  // Nostro & CSD
  NOSTRO_FUNDS_RECEIVED: 'nostro_funds_received',
  NOSTRO_FUNDS_TIMEOUT: 'nostro_funds_timeout',
  CSD_INSTRUCTION_SENT: 'csd_instruction_sent',
  CSD_CONFIRMED: 'csd_confirmed',
  CSD_REJECTED: 'csd_rejected',

  // Portfolio & Maturity
  PORTFOLIO_ACTIVE: 'portfolio_active',
  MATURITY_NOTICE_SENT: 'maturity_notice_sent',
  ROLLOVER_ELECTED: 'rollover_elected',
  REALISE_ELECTED: 'realise_elected',

  // Divestment & Repatriation
  DIVESTMENT_FX_RFQ_SENT: 'divestment_fx_rfq_sent',
  DIVESTMENT_FX_LOCKED: 'divestment_fx_locked',
  PROCEEDS_REPATRIATED: 'proceeds_repatriated',
  SETTLEMENT_COMPLETE: 'settlement_complete',
  REMOVED_FROM_PORTFOLIO: 'removed_from_portfolio',
});

/** Human-readable labels for each state */
export const STATE_LABELS = Object.freeze({
  [TRANSACTION_STATES.REGISTERED]: 'Registered',
  [TRANSACTION_STATES.KYC_SUBMITTED]: 'KYC Submitted',
  [TRANSACTION_STATES.KYC_MAKER_REVIEW]: 'KYC Maker Review',
  [TRANSACTION_STATES.KYC_MAKER_APPROVED]: 'KYC Maker Approved',
  [TRANSACTION_STATES.KYC_CHECKER_REVIEW]: 'KYC Checker Review',
  [TRANSACTION_STATES.KYC_CHECKER_APPROVED]: 'KYC Checker Approved',
  [TRANSACTION_STATES.KYC_REJECTED]: 'KYC Rejected',

  [TRANSACTION_STATES.CDS_CARD_PENDING]: 'CDS Card Pending',
  [TRANSACTION_STATES.CDS_CARD_CHECKER_SIGNED]: 'CDS Card Checker Signed',
  [TRANSACTION_STATES.CDS_CARD_MAKER_SIGNED]: 'CDS Card Maker Signed',
  [TRANSACTION_STATES.CDS_CARD_COMPLETE]: 'CDS Card Complete',
  [TRANSACTION_STATES.CDS_CARD_REJECTED]: 'CDS Card Rejected',

  [TRANSACTION_STATES.AUCTION_ELIGIBLE]: 'Auction Eligible',
  [TRANSACTION_STATES.BID_PLACED]: 'Bid Placed',
  [TRANSACTION_STATES.BID_ALLOCATED]: 'Bid Allocated',
  [TRANSACTION_STATES.BID_UNSUCCESSFUL]: 'Bid Unsuccessful',

  [TRANSACTION_STATES.SETTLEMENT_PENDING]: 'Settlement Pending',
  [TRANSACTION_STATES.FX_RFQ_SENT]: 'Foreign Exchange RFQ Sent',
  [TRANSACTION_STATES.FX_QUOTES_RECEIVED]: 'Foreign Exchange Quotes Received',
  [TRANSACTION_STATES.FX_RATE_LOCKED]: 'Foreign Exchange Rate Locked',
  [TRANSACTION_STATES.FX_RATE_EXPIRED]: 'Foreign Exchange Rate Expired',

  [TRANSACTION_STATES.BANK_INSTRUCTION_SENT]: 'Bank Instruction Sent',
  [TRANSACTION_STATES.BANK_INSTRUCTION_CONFIRMED]: 'Bank Instruction Confirmed',
  [TRANSACTION_STATES.BANK_INSTRUCTION_REJECTED]: 'Bank Instruction Rejected',

  [TRANSACTION_STATES.NOSTRO_FUNDS_RECEIVED]: 'Nostro Funds Received',
  [TRANSACTION_STATES.NOSTRO_FUNDS_TIMEOUT]: 'Nostro Funds Timeout',
  [TRANSACTION_STATES.CSD_INSTRUCTION_SENT]: 'CSD Instruction Sent',
  [TRANSACTION_STATES.CSD_CONFIRMED]: 'CSD Confirmed',
  [TRANSACTION_STATES.CSD_REJECTED]: 'CSD Rejected',

  [TRANSACTION_STATES.PORTFOLIO_ACTIVE]: 'Portfolio Active',
  [TRANSACTION_STATES.MATURITY_NOTICE_SENT]: 'Maturity Notice Sent',
  [TRANSACTION_STATES.ROLLOVER_ELECTED]: 'Rollover Elected',
  [TRANSACTION_STATES.REALISE_ELECTED]: 'Realise Elected',

  [TRANSACTION_STATES.DIVESTMENT_FX_RFQ_SENT]: 'Divestment Foreign Exchange RFQ Sent',
  [TRANSACTION_STATES.DIVESTMENT_FX_LOCKED]: 'Divestment Foreign Exchange Locked',
  [TRANSACTION_STATES.PROCEEDS_REPATRIATED]: 'Proceeds Repatriated',
  [TRANSACTION_STATES.SETTLEMENT_COMPLETE]: 'Settlement Complete',
  [TRANSACTION_STATES.REMOVED_FROM_PORTFOLIO]: 'Removed from Portfolio',
});

// ─── Failure / Timeout States ───────────────────────────────────────────
export const FAILURE_STATES = Object.freeze([
  TRANSACTION_STATES.KYC_REJECTED,
  TRANSACTION_STATES.CDS_CARD_REJECTED,
  TRANSACTION_STATES.BID_UNSUCCESSFUL,
  TRANSACTION_STATES.FX_RATE_EXPIRED,
  TRANSACTION_STATES.BANK_INSTRUCTION_REJECTED,
  TRANSACTION_STATES.NOSTRO_FUNDS_TIMEOUT,
  TRANSACTION_STATES.CSD_REJECTED,
]);

// ─── Terminal States ────────────────────────────────────────────────────
export const TERMINAL_STATES = Object.freeze([
  TRANSACTION_STATES.SETTLEMENT_COMPLETE,
  TRANSACTION_STATES.REMOVED_FROM_PORTFOLIO,
]);

// ─── Timeout Configuration ─────────────────────────────────────────────
const TIMEOUT_CONFIG = Object.freeze({
  [TRANSACTION_STATES.FX_RFQ_SENT]: {
    timeoutMs: 5 * 60 * 1000, // 5 minutes
    failureState: TRANSACTION_STATES.FX_RATE_EXPIRED,
  },
  [TRANSACTION_STATES.FX_QUOTES_RECEIVED]: {
    timeoutMs: 2 * 60 * 1000, // 2 minutes
    failureState: TRANSACTION_STATES.FX_RATE_EXPIRED,
  },
  [TRANSACTION_STATES.FX_RATE_LOCKED]: {
    timeoutMs: 30 * 60 * 1000, // 30 minutes
    failureState: TRANSACTION_STATES.FX_RATE_EXPIRED,
  },
  [TRANSACTION_STATES.BANK_INSTRUCTION_SENT]: {
    timeoutMs: 24 * 60 * 60 * 1000, // 24 hours
    failureState: TRANSACTION_STATES.BANK_INSTRUCTION_REJECTED,
  },
  [TRANSACTION_STATES.NOSTRO_FUNDS_RECEIVED]: {
    timeoutMs: 48 * 60 * 60 * 1000, // 48 hours
    failureState: TRANSACTION_STATES.NOSTRO_FUNDS_TIMEOUT,
  },
  [TRANSACTION_STATES.CSD_INSTRUCTION_SENT]: {
    timeoutMs: 24 * 60 * 60 * 1000, // 24 hours
    failureState: TRANSACTION_STATES.CSD_REJECTED,
  },
  [TRANSACTION_STATES.DIVESTMENT_FX_RFQ_SENT]: {
    timeoutMs: 5 * 60 * 1000, // 5 minutes
    failureState: TRANSACTION_STATES.FX_RATE_EXPIRED,
  },
});

// ─── Transition Map ─────────────────────────────────────────────────────
// Each key maps to an array of valid target states.
// Entries can be a plain string (any role) or { state, roles, guard? }

const S = TRANSACTION_STATES;

const TRANSITIONS = Object.freeze({
  // Registration & KYC
  [S.REGISTERED]: [
    S.KYC_SUBMITTED,
  ],

  [S.KYC_SUBMITTED]: [
    { state: S.KYC_MAKER_REVIEW, roles: ['bank_compliance_maker'] },
  ],

  [S.KYC_MAKER_REVIEW]: [
    { state: S.KYC_MAKER_APPROVED, roles: ['bank_compliance_maker'] },
    { state: S.KYC_REJECTED, roles: ['bank_compliance_maker'] },
  ],

  [S.KYC_MAKER_APPROVED]: [
    { state: S.KYC_CHECKER_REVIEW, roles: ['bank_compliance_checker'] },
  ],

  [S.KYC_CHECKER_REVIEW]: [
    { state: S.KYC_CHECKER_APPROVED, roles: ['bank_compliance_checker'], guard: 'notSameUserAsMaker' },
    { state: S.KYC_MAKER_REVIEW, roles: ['bank_compliance_checker'] }, // return to maker
    { state: S.KYC_REJECTED, roles: ['bank_compliance_checker'] },
  ],

  [S.KYC_CHECKER_APPROVED]: [
    S.CDS_CARD_PENDING,
  ],

  [S.KYC_REJECTED]: [
    S.KYC_SUBMITTED, // resubmission
  ],

  // CDS Card Signing
  [S.CDS_CARD_PENDING]: [
    { state: S.CDS_CARD_CHECKER_SIGNED, roles: ['bank_compliance_checker'] },
    { state: S.CDS_CARD_MAKER_SIGNED, roles: ['bank_compliance_maker'] },
    { state: S.CDS_CARD_REJECTED, roles: ['bank_compliance_maker', 'bank_compliance_checker'] },
  ],

  [S.CDS_CARD_CHECKER_SIGNED]: [
    { state: S.CDS_CARD_COMPLETE, roles: ['bank_compliance_maker'], guard: 'notSameUserAsMaker' },
    { state: S.CDS_CARD_REJECTED, roles: ['bank_compliance_maker'] },
  ],

  [S.CDS_CARD_MAKER_SIGNED]: [
    { state: S.CDS_CARD_COMPLETE, roles: ['bank_compliance_checker'], guard: 'notSameUserAsMaker' },
    { state: S.CDS_CARD_REJECTED, roles: ['bank_compliance_checker'] },
  ],

  [S.CDS_CARD_COMPLETE]: [
    S.AUCTION_ELIGIBLE,
  ],

  [S.CDS_CARD_REJECTED]: [
    S.CDS_CARD_PENDING, // retry
  ],

  // Auction & Bidding
  [S.AUCTION_ELIGIBLE]: [
    { state: S.BID_PLACED, roles: ['investor_family_office', 'investor_individual'] },
  ],

  [S.BID_PLACED]: [
    S.BID_ALLOCATED,
    S.BID_UNSUCCESSFUL,
  ],

  [S.BID_ALLOCATED]: [
    S.SETTLEMENT_PENDING,
  ],

  [S.BID_UNSUCCESSFUL]: [
    S.AUCTION_ELIGIBLE, // re-enter next auction
  ],

  // Settlement & FX
  [S.SETTLEMENT_PENDING]: [
    { state: S.FX_RFQ_SENT, roles: ['investor_family_office', 'investor_individual'] },
  ],

  [S.FX_RFQ_SENT]: [
    { state: S.FX_QUOTES_RECEIVED, roles: ['bank_treasury_dealer'] },
    S.FX_RATE_EXPIRED,
  ],

  [S.FX_QUOTES_RECEIVED]: [
    { state: S.FX_RATE_LOCKED, roles: ['investor_family_office', 'investor_individual'] },
    S.FX_RATE_EXPIRED,
  ],

  [S.FX_RATE_LOCKED]: [
    S.BANK_INSTRUCTION_SENT,
    S.FX_RATE_EXPIRED,
  ],

  [S.FX_RATE_EXPIRED]: [
    S.FX_RFQ_SENT, // retry
  ],

  // Bank Instructions
  [S.BANK_INSTRUCTION_SENT]: [
    { state: S.BANK_INSTRUCTION_CONFIRMED, roles: ['bank_treasury_dealer'] },
    { state: S.BANK_INSTRUCTION_REJECTED, roles: ['bank_treasury_dealer'] },
  ],

  [S.BANK_INSTRUCTION_CONFIRMED]: [
    S.NOSTRO_FUNDS_RECEIVED,
    S.NOSTRO_FUNDS_TIMEOUT,
  ],

  [S.BANK_INSTRUCTION_REJECTED]: [
    S.BANK_INSTRUCTION_SENT, // retry
  ],

  // Nostro & CSD
  [S.NOSTRO_FUNDS_RECEIVED]: [
    S.CSD_INSTRUCTION_SENT,
  ],

  [S.NOSTRO_FUNDS_TIMEOUT]: [
    S.BANK_INSTRUCTION_SENT, // retry
  ],

  [S.CSD_INSTRUCTION_SENT]: [
    S.CSD_CONFIRMED,
    S.CSD_REJECTED,
  ],

  [S.CSD_CONFIRMED]: [
    S.PORTFOLIO_ACTIVE,
  ],

  [S.CSD_REJECTED]: [
    S.CSD_INSTRUCTION_SENT, // retry
  ],

  // Portfolio & Maturity
  [S.PORTFOLIO_ACTIVE]: [
    S.MATURITY_NOTICE_SENT,
  ],

  [S.MATURITY_NOTICE_SENT]: [
    { state: S.ROLLOVER_ELECTED, roles: ['investor_family_office', 'investor_individual'] },
    { state: S.REALISE_ELECTED, roles: ['investor_family_office', 'investor_individual'] },
  ],

  [S.ROLLOVER_ELECTED]: [
    S.AUCTION_ELIGIBLE, // re-enter auction cycle
  ],

  [S.REALISE_ELECTED]: [
    S.DIVESTMENT_FX_RFQ_SENT,
  ],

  // Divestment & Repatriation
  [S.DIVESTMENT_FX_RFQ_SENT]: [
    { state: S.DIVESTMENT_FX_LOCKED, roles: ['investor_family_office', 'investor_individual'] },
    S.FX_RATE_EXPIRED,
  ],

  [S.DIVESTMENT_FX_LOCKED]: [
    S.PROCEEDS_REPATRIATED,
  ],

  [S.PROCEEDS_REPATRIATED]: [
    S.SETTLEMENT_COMPLETE,
  ],

  [S.SETTLEMENT_COMPLETE]: [
    S.REMOVED_FROM_PORTFOLIO,
  ],

  [S.REMOVED_FROM_PORTFOLIO]: [
    // Terminal state — no outgoing transitions
  ],
});

// ─── Bank Approval Sub-Machine ──────────────────────────────────────────
// Per-bank approval tracking for KYC/CDS dual-signature workflows

export const BANK_APPROVAL_STATES = Object.freeze({
  MAKER_REVIEW: 'maker_review',
  MAKER_APPROVED: 'maker_approved',
  MAKER_REJECTED: 'maker_rejected',
  CHECKER_REVIEW: 'checker_review',
  CHECKER_APPROVED: 'checker_approved',
  CHECKER_RETURNED: 'checker_returned',
});

const BA = BANK_APPROVAL_STATES;

export const BANK_APPROVAL_TRANSITIONS = Object.freeze({
  [BA.MAKER_REVIEW]: [
    { state: BA.MAKER_APPROVED, roles: ['bank_compliance_maker'] },
    { state: BA.MAKER_REJECTED, roles: ['bank_compliance_maker'] },
  ],
  [BA.MAKER_APPROVED]: [
    { state: BA.CHECKER_REVIEW, roles: ['bank_compliance_checker'] },
  ],
  [BA.CHECKER_REVIEW]: [
    { state: BA.CHECKER_APPROVED, roles: ['bank_compliance_checker'], guard: 'notSameUserAsMaker' },
    { state: BA.CHECKER_RETURNED, roles: ['bank_compliance_checker'] },
  ],
  [BA.CHECKER_APPROVED]: [
    // Terminal for sub-machine
  ],
  [BA.CHECKER_RETURNED]: [
    { state: BA.MAKER_REVIEW, roles: ['bank_compliance_maker'] },
  ],
  [BA.MAKER_REJECTED]: [
    // Terminal for sub-machine
  ],
});

// ─── Helpers ────────────────────────────────────────────────────────────

/**
 * Normalise a transition entry into { state, roles, guard }.
 * @param {string | { state: string, roles?: string[], guard?: string }} entry
 * @returns {{ state: string, roles: string[] | null, guard: string | null }}
 */
function normaliseEntry(entry) {
  if (typeof entry === 'string') return { state: entry, roles: null, guard: null };
  return {
    state: entry.state,
    roles: entry.roles ?? null,
    guard: entry.guard ?? null,
  };
}

// ─── Public API ─────────────────────────────────────────────────────────

/**
 * Can the transaction move from `currentState` to `targetState` (ignoring roles)?
 * @param {string} currentState
 * @param {string} targetState
 * @returns {boolean}
 */
export function canTransition(currentState, targetState) {
  const entries = TRANSITIONS[currentState];
  if (!entries) return false;
  return entries.some((e) => {
    const { state } = normaliseEntry(e);
    return state === targetState;
  });
}

/**
 * Return the states the transaction can move to from `currentState`,
 * filtered by the caller's role when transitions are role-restricted.
 * @param {string} currentState
 * @param {string} [userRole]
 * @returns {string[]}
 */
export function getAvailableTransitions(currentState, userRole) {
  const entries = TRANSITIONS[currentState];
  if (!entries) return [];

  return entries
    .map(normaliseEntry)
    .filter(({ roles }) => {
      if (!roles) return true;
      if (!userRole) return false;
      return roles.includes(userRole);
    })
    .map(({ state }) => state);
}

/**
 * Validate whether a transition is allowed and explain why not if invalid.
 * @param {string} currentState
 * @param {string} targetState
 * @param {string} [userRole]
 * @returns {{ valid: boolean, reason?: string, guard?: string }}
 */
export function validateTransition(currentState, targetState, userRole) {
  if (!TRANSITIONS[currentState]) {
    return { valid: false, reason: `Unknown current state: "${currentState}"` };
  }

  const allStates = Object.values(TRANSACTION_STATES);
  if (!allStates.includes(targetState)) {
    return { valid: false, reason: `Unknown target state: "${targetState}"` };
  }

  const entries = TRANSITIONS[currentState];
  const match = entries
    .map(normaliseEntry)
    .find(({ state }) => state === targetState);

  if (!match) {
    return {
      valid: false,
      reason: `Transition from "${currentState}" to "${targetState}" is not allowed`,
    };
  }

  if (match.roles) {
    if (!userRole) {
      return {
        valid: false,
        reason: `Transition to "${targetState}" requires one of these roles: ${match.roles.join(', ')}`,
      };
    }
    if (!match.roles.includes(userRole)) {
      return {
        valid: false,
        reason: `Role "${userRole}" cannot transition to "${targetState}". Required: ${match.roles.join(', ')}`,
      };
    }
  }

  // If there's a guard, return it so the caller can enforce it
  if (match.guard) {
    return { valid: true, guard: match.guard };
  }

  return { valid: true };
}

/**
 * Get timeout configuration for a state.
 * @param {string} state
 * @returns {{ timeoutMs: number, failureState: string } | null}
 */
export function getTimeoutConfig(state) {
  return TIMEOUT_CONFIG[state] || null;
}

/**
 * Check if a state is a failure/timeout state.
 * @param {string} state
 * @returns {boolean}
 */
export function isFailureState(state) {
  return FAILURE_STATES.includes(state);
}

/**
 * Check if a state is a terminal state.
 * @param {string} state
 * @returns {boolean}
 */
export function isTerminalState(state) {
  return TERMINAL_STATES.includes(state);
}
